import React, { useState } from "react";
import { Bell, Building2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { useSiteContext } from "./Layout";

export default function Header({ selectedSite, setSelectedSite, alertsCount = 0, alerts = [] }) {
  const { sites, orgs, selectedOrg, setSelectedOrg } = useSiteContext();
  const { user } = useAuth();
  const [bellOpen, setBellOpen] = useState(false);

  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm px-4 md:px-6 py-3 flex items-center gap-3 md:gap-4">
      <div className="flex-1 flex items-center gap-3 md:gap-4 min-w-0">
        {isPlatform && orgs?.length > 0 && (
          <Select value={selectedOrg || ""} onValueChange={(v) => setSelectedOrg(v)}>
            <SelectTrigger className="w-40 md:w-52 bg-orange-50 border-orange-200 h-10" data-testid="org-selector">
              <Building2 className="w-3.5 h-3.5 text-orange-500 shrink-0 mr-1" />
              <SelectValue placeholder="Select Tenant" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedSite} onValueChange={setSelectedSite}>
          <SelectTrigger className="w-44 md:w-64 bg-gray-50 border-gray-200 h-10" data-testid="site-selector">
            <SelectValue placeholder="Select Site" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All Sites</SelectItem>
            {sites?.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="relative">
        <button onClick={() => setBellOpen(!bellOpen)} className="relative w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 flex items-center justify-center" data-testid="notifications-btn">
          <Bell className="w-4 h-4 text-gray-600" strokeWidth={2.5} />
          {alertsCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 f360-gradient border-0 text-[10px]">{alertsCount}</Badge>
          )}
        </button>
        {bellOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setBellOpen(false)} />
            <div className="absolute right-0 top-12 w-80 bg-white rounded-lg border border-gray-200 z-20 overflow-hidden shadow-lg" data-testid="notif-dropdown">
              <div className="px-4 py-3 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">Recent Alerts</div>
              <div className="max-h-80 overflow-y-auto">
                {alerts.slice(0, 5).map((a) => (
                  <Link key={a.alert_id} to="/alerts" onClick={() => setBellOpen(false)} className="block px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-red-500 status-pulse-red" : a.severity === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
                      <span className="text-sm text-gray-900 truncate flex-1">{a.title}</span>
                    </div>
                    <div className="text-[10px] text-gray-500 ml-4">{new Date(a.created_at).toLocaleTimeString("en-IN")}</div>
                  </Link>
                ))}
                {!alerts.length && <div className="px-4 py-6 text-center text-xs text-gray-500">No alerts</div>}
              </div>
              <Link to="/alerts" onClick={() => setBellOpen(false)} className="block text-center py-2 text-xs text-orange-600 border-t border-gray-200 hover:bg-gray-50">View all →</Link>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-3 pl-2 md:pl-4 md:border-l border-gray-200">
        <div className="text-right hidden md:block">
          <div className="text-sm font-medium text-gray-900">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-wider f360-gradient-text font-semibold">{user?.role?.replace("_", " ")}</div>
        </div>
        <div className="w-9 h-9 rounded-full f360-gradient flex items-center justify-center text-white font-bold text-sm">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
      </div>

      {/* Platform brand sits at the header's right edge — the sidebar's brand slot shows the
          client organisation's own logo instead. */}
      <div className="hidden sm:flex items-center pl-3 md:pl-4 border-l border-gray-200" title="Fire360Pro ACMS">
        <img src="/logo.jpg" alt="Fire360Pro" className="h-8 w-auto" />
      </div>
    </header>
  );
}
